<template>
  <v-autocomplete
    v-bind="$attrs"
    ref="field"
    v-model="localValue"
    :clearable="clearable && !$attrs.readonly && $attrs.readonly !== ''"
    :items="items"
    :loading="loading"
    no-filter
    :search-input.sync="match"
    hide-selected
    @change="onChange"
    @focus="(e) => e.target.click()"
  >
    <template #selection="data">
      <v-chip
        color="white"
        :selected="data.selected"
        class="chip--select-multi"
        :close="$attrs.multiple"
        @input="removeItem(data.item)"
      >
        <v-avatar>
          <img :src="data.item.logo" />
        </v-avatar>
        <v-list-tile-content>
          <v-list-tile-title>
            <span>{{ data.item.text }}</span>
          </v-list-tile-title>

          <v-list-tile-sub-title class="caption primary--text text--lighten-1">
            {{ data.item.category }}
          </v-list-tile-sub-title>
        </v-list-tile-content>
      </v-chip>
    </template>
    <template v-if="allowCreate" #prepend-item>
      <v-list>
        <v-list-item-content>
          <v-list-item-title>
            <v-btn small color="primary" block @click="newTeam">
              {{ $t('action.buttons.create') }} ...
            </v-btn>
          </v-list-item-title>
        </v-list-item-content>
      </v-list>
    </template>
    <template #item="data">
      <v-list-tile-avatar>
        <img :src="data.item.logo" />
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-title v-html="data.item.text" />
        <v-list-tile-sub-title class="caption primary--text text--lighten-1">
          {{ data.item.category }}
        </v-list-tile-sub-title>
      </v-list-tile-content>
    </template>
    <template slot="prepend">
      <slot name="prepend" />
    </template>
  </v-autocomplete>
</template>

<script>
import { debounce } from 'debounce'
export default {
  name: 'TeamField',
  props: {
    value: {
      type: [String, Array],
      required: false,
      default: '',
    },
    _id: {
      type: [String, Array],
      required: false,
      default: '',
    },
    categoryId: {
      type: String,
      required: false,
      default: '',
    },
    tournamentId: {
      type: String,
      required: false,
      default: '',
    },
    entityId: {
      type: String,
      required: false,
      default: '',
    },
    isCustomer: {
      type: Boolean,
      required: false,
      default: false,
    },
    all: {
      type: Boolean,
      required: false,
      default: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    hideItems: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
      require: false,
    },
  },
  data() {
    return {
      match: '',
      itemList: [],
      cacheList: [],
      loading: false,
      init: false,
    }
  },

  computed: {
    localValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    items() {
      return this.itemList
    },
  },
  watch: {
    value(val) {
      if (!val.length) this.$emit('changeObjects', [])
    },
    match() {
      this.fetch()
    },
    _id() {
      this.fetch()
    },
    categoryId() {
      this.fetch()
    },
    tournamentId() {
      this.fetch()
    },
    entityId() {
      this.fetch()
    },
    isCustomer() {
      this.fetch()
    },
    all(val, oldVal) {
      if (!val && oldVal) this.localValue = ''
      this.fetch()
    },
  },
  created() {
    this.fetch = debounce(function () {
      if (this.$refs.field && this.value !== this.$refs.field.lazyValue)
        this.$refs.field.lazyValue = this.value

      let params = { set: false }
      if (this._id) params._id = this._id
      else if (this.localValue && !Array.isArray(this.localValue)) {
        params._id = this.localValue
      } else if (
        Array.isArray(this.localValue) &&
        this.localValue.length &&
        !this.init
      ) {
        params._id = this.localValue
      }

      if (this.entityId) params.entityId = this.entityId

      if (this.categoryId) params.categoryId = this.categoryId
      else if (this.tournamentId) params.tournamentId = this.tournamentId

      if (this.match) params.match = this.match
      if (this.isCustomer) params.isCustomer = this.isCustomer

      if (this.all) params.all = this.all
      this.loading = true
      this.$store
        .dispatch('teams/fetchSelectList', params)
        .then((list) => {
          this.itemList = []
          list.forEach((item) => {
            if (item.category && Array.isArray(item.category))
              item.category = item.category.join(' - ')
            this.itemList.push(item)
          })
          if (
            !Array.isArray(this.value) &&
            !this.itemList.some((item) => item.value === this.value)
          )
            this.value = ''
          this.setCache()
          if (this.localValue.length) this.onChange(this.localValue)
        })
        .finally(() => {
          this.loading = false
          this.init = true
        })
    }, 700)
    this.fetch()
  },
  methods: {
    fetch() {},
    onChange(val) {
      this.$emit('change', val)
      this.$emit(
        'changeObjects',
        val
          ? this.itemList.filter((item) => val && val.includes(item.value))
          : []
      )
    },
    setCache() {
      if (Array.isArray(this.localValue)) {
        this.localValue.forEach((v) => {
          const isCached = this.cacheList.some((item) => item.value === v)
          if (!isCached) {
            let item = this.itemList.find((item) => item.value === v)
            if (item) {
              this.cacheList.push(item)
            }
          }
        })
        this.cacheList = this.cacheList.filter((item) =>
          this.localValue.includes(item.value)
        )
        this.cacheList.forEach((cacheItem) => {
          const inList = this.itemList.some(
            (listItem) => listItem.value === cacheItem.value
          )
          if (!inList) this.itemList.push(cacheItem)
        })
      }
    },
    removeItem(item) {
      if (Array.isArray(this.localValue)) {
        const index = this.localValue.indexOf(item.value)
        if (index >= 0) this.localValue.splice(index, 1)
      } else this.localValue = ''
    },
    newTeam() {
      this.$emit('openModal')
      this.$refs.field.blur()
    },
  },
}
</script>
